// Password reset landing page — /auth/supabase/reset
//
// Flow:
//   1. User receives a password-reset email and clicks the link.
//   2. Supabase sends them here with a recovery session in the URL.
//   3. We verify the session is a recovery session via getUser().
//   4. If valid: show "Set a new password" form.
//   5. On success: bounce to /map after 1 second.
//
// Same defensive structure as SupabaseAuthCallback: handles expired
// links, missing sessions, and form errors gracefully.

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, Eye, EyeOff, Loader2, AlertCircle } from '../shell/Icon';
import { supabase } from '../lib/supabase';
import { useAuthStore } from '../stores/authStore';

type PageState = 'checking' | 'ready' | 'success' | 'expired' | 'error';

export default function SupabaseAuthReset() {
  const nav = useNavigate();
  const { updatePassword } = useAuthStore();

  const [pageState, setPageState] = useState<PageState>('checking');
  const [checkError, setCheckError] = useState<string | null>(null);

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [showConfirmPw, setShowConfirmPw] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  // On mount: exchange any code in the URL, then verify we have a
  // recovery session before showing the form.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!supabase) {
        setCheckError('Supabase not configured');
        setPageState('error');
        return;
      }

      try {
        const href = typeof window !== 'undefined' ? window.location.href : '';
        const url = new URL(href);

        // Supabase PKCE recovery link carries ?code= — exchange it first.
        const code = url.searchParams.get('code');
        if (code) {
          const { error } = await supabase.auth.exchangeCodeForSession(href);
          if (cancelled) return;
          if (error) {
            setCheckError(
              error.message?.toLowerCase().includes('code verifier')
                ? 'Please open this link in the same browser where you requested the reset.'
                : error.message,
            );
            setPageState('expired');
            return;
          }
        }

        // Confirm we now have a user session.
        const { data, error: userErr } = await supabase.auth.getUser();
        if (cancelled) return;
        if (userErr || !data.user) {
          setPageState('expired');
          return;
        }

        setPageState('ready');
      } catch (e: unknown) {
        if (cancelled) return;
        setCheckError(e instanceof Error ? e.message : String(e));
        setPageState('error');
      }
    })();
    return () => { cancelled = true; };
  }, []);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setFormError(null);

    if (newPassword.length < 8) {
      setFormError('Password must be at least 8 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      setFormError('Passwords don\'t match');
      return;
    }

    setSubmitting(true);
    const result = await updatePassword(newPassword);
    setSubmitting(false);

    if (!result.ok) {
      setFormError(result.error ?? 'Failed to update password');
      return;
    }

    setPageState('success');
    setTimeout(() => nav('/map', { replace: true }), 1000);
  }

  return (
    <div className="absolute inset-0 bg-cockpit-900 flex items-center justify-center p-6">
      <div className="max-w-md w-full bg-cockpit-800 border border-cockpit-700 rounded-2xl px-5 py-6 space-y-4">
        <div className="flex flex-col items-center gap-2 text-center">
          <img
            src="/icon-192.png"
            alt="Fyrefly"
            className="h-14 w-14 rounded-xl"
          />
          <h1 className="text-lg font-semibold text-cockpit-100">
            {pageState === 'success' ? 'Password updated ✓' : 'Set a new password'}
          </h1>
        </div>

        {/* Checking */}
        {pageState === 'checking' && (
          <div className="flex justify-center py-4">
            <Loader2 size={22} className="animate-spin text-cockpit-400" />
          </div>
        )}

        {/* Expired / invalid link */}
        {(pageState === 'expired' || pageState === 'error') && (
          <div className="space-y-3 text-center">
            <div className="flex justify-center">
              <AlertCircle size={28} className="text-red-400" />
            </div>
            <p className="text-sm text-cockpit-300">
              {checkError ?? 'Reset link expired or invalid. Please request a new one.'}
            </p>
            <button
              onClick={() => nav('/settings', { replace: true })}
              className="bg-ember-600 hover:bg-ember-500 text-white text-sm rounded-lg px-4 py-2"
            >
              Back to Settings
            </button>
          </div>
        )}

        {/* Ready — show form */}
        {pageState === 'ready' && (
          <form onSubmit={handleSubmit} className="space-y-3">
            {/* New password */}
            <div className="relative">
              <Lock size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-cockpit-400 pointer-events-none" />
              <input
                type={showPw ? 'text' : 'password'}
                autoComplete="new-password"
                placeholder="New password (min 8 chars)"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                disabled={submitting}
                required
                className="w-full bg-cockpit-700 border border-cockpit-600 text-cockpit-100 placeholder-cockpit-500 rounded-lg pl-9 pr-10 py-2.5 text-sm focus:outline-none focus:border-ember-500 disabled:opacity-50"
              />
              <button
                type="button"
                tabIndex={-1}
                onClick={() => setShowPw((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-cockpit-400 hover:text-cockpit-200"
              >
                {showPw ? <EyeOff size={14} /> : <Eye size={14} />}
              </button>
            </div>

            {/* Confirm password */}
            <div className="relative">
              <Lock size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-cockpit-400 pointer-events-none" />
              <input
                type={showConfirmPw ? 'text' : 'password'}
                autoComplete="new-password"
                placeholder="Confirm new password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                disabled={submitting}
                required
                className="w-full bg-cockpit-700 border border-cockpit-600 text-cockpit-100 placeholder-cockpit-500 rounded-lg pl-9 pr-10 py-2.5 text-sm focus:outline-none focus:border-ember-500 disabled:opacity-50"
              />
              <button
                type="button"
                tabIndex={-1}
                onClick={() => setShowConfirmPw((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-cockpit-400 hover:text-cockpit-200"
              >
                {showConfirmPw ? <EyeOff size={14} /> : <Eye size={14} />}
              </button>
            </div>

            {formError && (
              <div className="flex items-start gap-2 text-xs text-red-400">
                <AlertCircle size={13} className="mt-0.5 shrink-0" />
                <span>{formError}</span>
              </div>
            )}

            <button
              type="submit"
              disabled={submitting || !newPassword || !confirmPassword}
              className="w-full bg-ember-600 hover:bg-ember-500 disabled:opacity-50 text-white font-medium text-sm rounded-lg py-2.5 flex items-center justify-center gap-2 transition-colors"
            >
              {submitting ? <Loader2 size={15} className="animate-spin" /> : null}
              Update password
            </button>
          </form>
        )}

        {/* Success */}
        {pageState === 'success' && (
          <div className="text-center space-y-2">
            <p className="text-sm text-cockpit-300">Password updated. Signing you in…</p>
            <Loader2 size={18} className="animate-spin text-cockpit-400 mx-auto" />
          </div>
        )}
      </div>
    </div>
  );
}
